/**
 * CoPilotGuide Component
 *
 * Main orchestrator for deterministic CoPilot Guide flows.
 * Processes guide configs (tasks -> chats -> messages/features/confirmations),
 * manages chat state, renders widgets via the widget registry, and handles transitions.
 *
 * Features:
 * - Timeout cleanup on unmount (no memory leaks)
 * - Error boundary around widget rendering
 * - ARIA live regions for screen reader announcements
 * - Keyboard navigation for action buttons
 * - Session persistence via localStorage
 * - Analytics/telemetry hooks (onAnalyticsEvent)
 */

import { ArrowLeft, RotateCcw, Sparkles, XCircle } from '@tonnie/assets';
import { Button, Card, CardHeader, CardTitle } from '@tonnie/ui';
import { useEffect, useId, useRef } from 'react';

import { GuideChat } from './GuideChat';
import { StepIndicator } from './StepIndicator';
import { useRenderWidget } from './GuideWidgetRenderer';
import { useCoPilotGuide } from './hooks/use-co-pilot-guide';

// Re-export public types for external consumers
export type {
  InstantiationState,
  GuideAnalyticsEventType,
  GuideAnalyticsEvent,
  GuideTraceEventType,
  GuideTraceEvent,
  GuideCompletePayload,
  ICoPilotGuideProps,
} from './copilot-guide.types';

export function CoPilotGuide(props: import('./copilot-guide.types').ICoPilotGuideProps) {
  const { className = '' } = props;

  const {
    engine,
    chatMessages,
    isLoading,
    isAnimating,
    instantiationState,
    currentFeature,
    announcement,
    canGoBack,
    chatActions,
    handleChatAction,
    handleGoBack,
    handleRestart,
    handleKeyDown,
    handleInstantiationContinue,
    handleInstantiationRetry,
    handleFeatureComplete,
    handleFeatureCancel,
    t,
  } = useCoPilotGuide(props);

  const restartHintId = useId();
  const discardHintId = useId();

  const renderWidget = useRenderWidget({
    currentFeature,
    onFeatureComplete: handleFeatureComplete,
    onFeatureCancel: handleFeatureCancel,
  });

  // ── Prevent focus-induced ancestor scrolling ──
  const guideRootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = guideRootRef.current;
    if (!root) return;

    let savedScrolls: { el: Element; top: number; left: number }[] = [];

    const capture = () => {
      savedScrolls = [];
      let el: Element | null = root.parentElement;
      while (el) {
        if (el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth) {
          savedScrolls.push({ el, top: el.scrollTop, left: el.scrollLeft });
        }
        el = el.parentElement;
      }
    };

    const restore = () => {
      if (savedScrolls.length === 0) return;
      requestAnimationFrame(() => {
        for (const s of savedScrolls) {
          if (s.el.scrollTop !== s.top || s.el.scrollLeft !== s.left) {
            s.el.scrollTop = s.top;
            s.el.scrollLeft = s.left;
          }
        }
      });
    };

    root.addEventListener('pointerdown', capture, true);
    root.addEventListener('focusin', restore);
    return () => {
      root.removeEventListener('pointerdown', capture, true);
      root.removeEventListener('focusin', restore);
    };
  }, []);

  return (
    <div
      ref={guideRootRef}
      className={`${className} relative flex flex-col min-h-0`}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label={t('copilot.guideTitle')}
      data-testid="copilot-guide"
    >
      {/* ARIA live region for screen reader announcements */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      <Card className="border-border/50 shadow-sm flex-1 min-h-0 flex flex-col gap-0 py-0 overflow-hidden rounded-xl">
        {/* Header with step indicator */}
        <CardHeader className="shrink-0 border-b border-border py-2 px-4 bg-card rounded-t-xl">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              {canGoBack && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={handleGoBack}
                  aria-label={t('copilot.goBack')}
                  data-testid="guide-back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <StepIndicator engine={engine} />
            </div>
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm font-semibold leading-none hidden sm:block">
                {t('copilot.guideTitle')}
              </CardTitle>
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 overflow-hidden bg-primary/10 border border-primary/20 relative">
                <Sparkles className="w-4 h-4 text-primary-accessible absolute" aria-hidden="true" />
                <img
                  src="/assets/images/heroes/avatar-copilot.png"
                  alt={t('copilot.guide.avatarAlt')}
                  width={32}
                  height={32}
                  className="object-contain w-full h-full relative z-10"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </CardHeader>

        {/* Chat area */}
        <div
          className="flex-1 min-h-0 overflow-hidden flex flex-col bg-secondary/50"
          role="log"
          aria-label={t('copilot.chatLog')}
        >
          <GuideChat
            messages={chatMessages}
            hideInput
            onSendMessage={() => {
              /* noop – input hidden */
            }}
            isLoading={isLoading && !instantiationState}
            actions={chatActions}
            onActionSelect={handleChatAction}
            isAnimating={isAnimating}
            blendedWidget={currentFeature?.displayMode === 'blended' ? renderWidget() : undefined}
            blendedWidgetKey={
              currentFeature?.displayMode === 'blended' ? currentFeature.chatId : null
            }
            instantiationState={instantiationState}
            onInstantiationContinue={handleInstantiationContinue}
            onInstantiationRetry={handleInstantiationRetry}
          />
        </div>

        {/* Bottom toolbar — Restart + (optional) Discard.
         *
         * Visual hierarchy was the #1 footer-action complaint in the
         * 2026-04-28 UX review. Pre-fix, both buttons rendered as
         * `text-muted-foreground/60` text-only — invisible on light
         * backgrounds, fails WCAG AA, and gave Restart and Discard
         * the same weight even though Discard is destructive.
         *
         * The new toolbar:
         *   • Restart  — outline-style chip, neutral tone, RotateCcw icon
         *   • Discard  — ghost-style chip, destructive-colored, XCircle icon
         * Both meet AA contrast at rest (text-foreground / text-destructive-accessible),
         * both keep their existing keyboard semantics + data-testid.
         *
         * Restart prefers the host's `onRestart` (which opens the
         * FlowLifecycleManager's confirm modal). Falls back to the
         * engine's internal `handleRestart` for hosts that don't wrap
         * the guide in a manager (rare; legacy path).
         *
         * Discard renders only when the host provides `onCancel` —
         * typically wired to `controls.startDiscard()`. This is the
         * user-facing "throw this attempt away" affordance.
         *
         * Copy: "Restart" / "Discard" — canonical verbs (radar UX
         * Phase C-5 / C-3). Replaces "Restart conversation" /
         * "Cancel and exit" which mixed verb spaces.
         */}
        <div className="shrink-0 flex flex-wrap items-center justify-center gap-3 py-3 px-4 bg-card rounded-b-xl border-t border-border/40">
          <button
            type="button"
            onClick={() => (props.onRestart ? props.onRestart() : handleRestart())}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/60 hover:border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            data-testid="guide-restart"
            aria-describedby={restartHintId}
          >
            <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
            {t('copilot.lifecycle.toolbar.restart')}
            {/* Named by its verb; what it does is its description (WCAG 2.5.3). */}
            <span id={restartHintId} hidden>
              {t('copilot.lifecycle.toolbar.restartHint')}
            </span>
          </button>
          {props.onCancel && (
            <button
              type="button"
              onClick={props.onCancel}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-destructive-accessible hover:bg-destructive/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              data-testid="guide-cancel"
              aria-describedby={discardHintId}
            >
              <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
              {t('copilot.lifecycle.toolbar.discard')}
              <span id={discardHintId} hidden>
                {t('copilot.lifecycle.toolbar.discardHint')}
              </span>
            </button>
          )}
        </div>
      </Card>

      {/* Modal widgets rendered outside the card */}
      {currentFeature?.displayMode === 'modal' && renderWidget()}
    </div>
  );
}
